<template>
  <div
    class="mx-auto flex w-full flex-col items-center gap-5 md:gap-7.5 xl:gap-10"
  >
    <h2
      class="bp1440-text-40 text-center font-header text-xl leading-[110%] md:text-2.5xl md:leading-[105%] xl:text-4.5xl 2xl:text-[47px]/[49px]"
    >
      {{ t('common.archive') }}
    </h2>

    <div class="flex w-full flex-col border-b border-gray">
      <!-- First 5 years always visible -->
      <template v-for="year in firstYears" :key="year">
        <LazyAccordion :title="year.toString()" :is-embedded="true">
          <LazyEventList
            :items="normalizedArchiveData[year]?.items"
            :has-more="normalizedArchiveData[year]?.hasMore"
            @load-more="emit('loadMore', year)"
            :is-archive="true"
            :is-notice="isNotice"
          />
        </LazyAccordion>
      </template>

      <!-- Years 6+ in collapsible area -->
      <div
        v-if="hasExtraYears"
        :class="[
          'grid w-full transition-all duration-400 ease-in-out',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        ]"
      >
        <div class="flex min-h-0 flex-col overflow-hidden">
          <template v-for="year in extraYears" :key="year">
            <LazyAccordion :title="year.toString()" :is-embedded="true">
              <LazyEventList
                :items="normalizedArchiveData[year]?.items"
                :has-more="normalizedArchiveData[year]?.hasMore"
                @load-more="emit('loadMore', year)"
                :is-archive="true"
                :is-notice="isNotice"
              />
            </LazyAccordion>
          </template>
        </div>
      </div>
    </div>

    <ShowMoreButton
      v-if="hasExtraYears || hasMoreYears"
      :is-expanded="isExpanded && !hasMoreYears"
      type="border"
      class="mt-2"
      @click="onShowMoreClick"
    />
  </div>
</template>

<script setup lang="ts">
import type { ArchiveItem } from '~/types'

interface Props {
  archiveData:
    | Record<number, { items: ArchiveItem[]; hasMore?: boolean }>
    | ArchiveItem[]
    | any
  years: number[]
  hasMoreYears?: boolean
  isNotice?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  hasMoreYears: false,
})

const { t } = useI18n()
const emit = defineEmits<{
  (e: 'loadMore', year: number): void
  (e: 'loadMoreYears'): void
}>()

const isExpanded = ref(false)

const normalizedArchiveData = computed(() => {
  if (!props.archiveData) return {}

  if (!Array.isArray(props.archiveData)) {
    return props.archiveData
  }

  const groupedData: Record<
    number,
    { items: ArchiveItem[]; hasMore: boolean }
  > = {}

  props.archiveData.forEach((event: ArchiveItem & { year: number }) => {
    if (!event.year) return

    if (!groupedData[event.year]) {
      groupedData[event.year] = {
        items: [],
        hasMore: false,
      }
    }
    groupedData[event.year].items.push(event)
  })

  return groupedData
})

const visibleYears = computed(() =>
  props.years.filter((year) => {
    const yearData = normalizedArchiveData.value[year]
    return yearData && yearData.items && yearData.items.length > 0
  }),
)

const firstYears = computed(() => visibleYears.value.slice(0, 5))
const extraYears = computed(() => visibleYears.value.slice(5))
const hasExtraYears = computed(() => extraYears.value.length > 0)

const onShowMoreClick = () => {
  if (!isExpanded.value) {
    // First click: expand the section and load next batch
    isExpanded.value = true
    emit('loadMoreYears')
  } else if (props.hasMoreYears) {
    // Already expanded but more years available: load next batch, stay expanded
    emit('loadMoreYears')
  } else {
    // Fully loaded: toggle collapse
    isExpanded.value = !isExpanded.value
  }
}
</script>
